import { useEffect, useState, useCallback } from 'react';
import {
  Coins, AlertCircle, RefreshCw, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { listSales, getSalesMetrics, type SaleRow, type SalesMetrics } from '@/shared/services/adminService';
import { DataTable, type DataTableColumn } from '@/shared/components/ui/DataTable';
import { useTableSort } from '@/shared/hooks/useTableSort';

const PAGE_SIZE = 25;

const STATUS_LABELS: Record<number, { label: string; color: string }> = {
  0: { label: 'Pendente',  color: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' },
  1: { label: 'Aprovada',  color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  2: { label: 'Rejeitada', color: 'bg-red-500/15 text-red-400 border-red-500/30' },
};

const salesCols = (): DataTableColumn<SaleRow>[] => [
  { key: 'id', label: 'ID', width: '60px', sortKey: 'id',
    cell: (r) => <span className="text-xs font-mono text-white/55">{r.id}</span> },
  { key: 'kind', label: 'Tipo', width: '100px', sortKey: 'kind',
    cell: (r) => r.kind === 'servico'
      ? <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold border bg-violet-500/15 text-violet-300 border-violet-500/30">Serviço</span>
      : <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold border bg-sky-500/15 text-sky-300 border-sky-500/30">Recarga</span> },
  { key: 'account', label: 'Conta', width: '1.4fr', sortKey: 'accountId',
    cell: (r) => <span className="text-sm font-bold text-white truncate">{r.accountId || `UN ${r.userNum}`}</span> },
  { key: 'title', label: 'Item', width: '1.6fr', sortKey: 'title',
    cell: (r) => <span className="text-xs text-white/75 truncate">{r.title || (r.kitId != null ? `#${r.kitId}` : '—')}</span> },
  { key: 'gateway', label: 'Gateway', width: '120px', sortKey: 'gateway',
    cell: (r) => r.gateway
      ? <span className="text-[11px] font-semibold text-sky-300">{r.gateway}</span>
      : <span className="text-[11px] text-white/30">—</span> },
  { key: 'brl', label: 'Valor BRL', width: '110px', sortKey: 'brl',
    cell: (r) => <span className="text-sm font-bold text-emerald-400">R$ {(r.brl ?? 0).toFixed(2)}</span> },
  { key: 'status', label: 'Status', width: '110px', sortKey: 'status',
    cell: (r) => {
      const s = STATUS_LABELS[r.status ?? -1] || { label: `St ${r.status}`, color: 'bg-white/5 text-white/55' };
      return <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold border ${s.color}`}>{s.label}</span>;
    } },
  { key: 'date', label: 'Data', width: '140px', sortKey: 'date',
    cell: (r) => <span className="text-[11px] text-white/55 font-mono">{r.date ? new Date(r.date).toLocaleString('pt-BR') : '—'}</span> },
];

export default function VendasPage() {
  const [items, setItems] = useState<SaleRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [kindFilter, setKindFilter] = useState<'recarga' | 'servico' | null>(null);
  const [statusFilter, setStatusFilter] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<SalesMetrics | null>(null);
  const { sorted, sort, toggle } = useTableSort(items, { key: 'date', order: 'desc' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listSales({
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE,
        kind: kindFilter ?? undefined,
        status: statusFilter ?? undefined,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro');
    } finally {
      setLoading(false);
    }
  }, [page, kindFilter, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { getSalesMetrics().then(setMetrics).catch(() => {}); }, []);

  const brl = (v?: number) => `R$ ${(v ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
          <Coins className="text-[#00e1d7]" size={26} />
          Vendas
        </h1>
        <p className="text-sm text-white/65 mt-1">Recargas e serviços — histórico unificado de vendas.</p>
      </div>

      {/* Cards métricos — recargas + serviços pagos */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <div className="surface-card rounded-xl p-4">
          <p className="text-[11px] uppercase tracking-wider text-white/55 font-bold">Vendido Hoje</p>
          <p className="text-2xl font-black text-emerald-400 mt-1 leading-none">{metrics ? brl(metrics.soldToday) : '—'}</p>
        </div>
        <div className="surface-card rounded-xl p-4">
          <p className="text-[11px] uppercase tracking-wider text-white/55 font-bold">Vendido no Mês</p>
          <p className="text-2xl font-black text-[#00e1d7] mt-1 leading-none">{metrics ? brl(metrics.soldMonth) : '—'}</p>
        </div>
        <div className="surface-card rounded-xl p-4">
          <p className="text-[11px] uppercase tracking-wider text-white/55 font-bold">Maior Gastador</p>
          <p className="text-lg font-black text-white mt-1 leading-none truncate">
            {metrics?.topAccountId || (metrics?.topUserNum ? `UN ${metrics.topUserNum}` : '—')}
          </p>
          <p className="text-[11px] mt-1 font-semibold text-[#00e1d7]">{metrics ? brl(metrics.topTotal) + ' no total' : ''}</p>
        </div>
      </div>

      {/* Filtros numa linha: tipo (botões) + status (dropdown) + refresh */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <TabBtn active={kindFilter === null} onClick={() => { setKindFilter(null); setPage(0); }}>Todas</TabBtn>
        <TabBtn active={kindFilter === 'recarga'} onClick={() => { setKindFilter('recarga'); setPage(0); }}>Recargas</TabBtn>
        <TabBtn active={kindFilter === 'servico'} onClick={() => { setKindFilter('servico'); setPage(0); }}>Serviços</TabBtn>
        <select
          value={statusFilter ?? ''}
          onChange={(e) => { setStatusFilter(e.target.value === '' ? null : Number(e.target.value)); setPage(0); }}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#0d141b] border border-white/[0.08]
                     text-white/85 outline-none focus:border-[#00e1d7]/40 cursor-pointer"
        >
          <option value="">Todos status</option>
          <option value="0">Pendentes</option>
          <option value="1">Aprovadas</option>
          <option value="2">Rejeitadas</option>
        </select>
        <button onClick={fetchData} disabled={loading}
          className="ml-auto flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold
                     bg-white/[0.04] hover:bg-white/[0.08] text-white/75 transition cursor-pointer">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {error && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <DataTable<SaleRow>
        columns={salesCols()}
        rows={sorted}
        sort={sort}
        onSort={toggle}
        rowKey={(r) => `${r.kind}-${r.id}`}
        loading={loading}
        empty="Nenhuma venda"
        mobileTitle={(r) => (
          <span className="flex items-center gap-2">
            {r.accountId || `UN ${r.userNum}`}
            <span className="text-[10px] text-white/45 font-mono">{r.kind === 'servico' ? 'serviço' : 'recarga'} #{r.id}</span>
          </span>
        )}
        mobileSubtitle={(r) => `R$ ${(r.brl ?? 0).toFixed(2)} · ${r.date ? new Date(r.date).toLocaleString('pt-BR') : '—'}`}
      />

      <div className="flex items-center justify-between mt-4 text-xs text-white/55">
        <span>Página {page + 1} de {totalPages} · {total.toLocaleString('pt-BR')} no total</span>
        <div className="flex gap-2">
          <button onClick={() => page > 0 && setPage(page - 1)} disabled={page === 0 || loading}
            className="px-3 py-1.5 rounded-lg bg-[#161e26] border border-white/[0.06]
                       hover:border-[#00e1d7]/40 disabled:opacity-40 disabled:cursor-not-allowed
                       transition cursor-pointer flex items-center gap-1">
            <ChevronLeft size={14} /> Anterior
          </button>
          <button onClick={() => page + 1 < totalPages && setPage(page + 1)} disabled={page + 1 >= totalPages || loading}
            className="px-3 py-1.5 rounded-lg bg-[#161e26] border border-white/[0.06]
                       hover:border-[#00e1d7]/40 disabled:opacity-40 disabled:cursor-not-allowed
                       transition cursor-pointer flex items-center gap-1">
            Próxima <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer whitespace-nowrap ${
        active ? 'bg-[#00e1d7]/15 text-[#00e1d7]' : 'text-white/55 hover:text-white hover:bg-white/[0.03]'
      }`}>
      {children}
    </button>
  );
}

